import { usePermissions } from '@/shared/hooks/usePermissions';
import { USER_PERMISSIONS } from '@/shared/types/permissions';
import { ClearFiltersButton } from '@/shared/components/Filters';
import { PageLayout } from '@/shared/components/PageLayout';
import { Pagination } from '@/shared/components/Pagination';
import { ValidationErrorDisplay } from '@/shared/components/ValidationErrorDisplay';
import {
  CAlert,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CForm,
  CFormInput,
  CFormLabel,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from '@coreui/react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useOwnRoleLevel } from '../hooks/useOwnRoleLevel';
import { useRoles } from '../hooks/useRoles';

export const RolesList = () => {
  const list = useRoles();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission(USER_PERMISSIONS.UserRolesEdit);
  const { ownLevel } = useOwnRoleLevel();
  const [search, setSearch] = useState(list.filters.search || '');

  return (
    <PageLayout>
      <CCard>
        <CCardHeader className="d-flex justify-content-between align-items-center">
          <h4 className="mb-0">Roles Management</h4>
          {canEdit && (
            <Link className="btn btn-primary" to="/users/roles/new">
              Add new role
            </Link>
          )}
        </CCardHeader>
        <CCardBody>
          {list.error && (
            <CAlert color="danger">
              <ValidationErrorDisplay
                message={
                  typeof list.error === 'string'
                    ? list.error
                    : list.error.message || 'Failed to load roles'
                }
              />
            </CAlert>
          )}
          <CForm
            className="d-flex align-items-center gap-2 mb-3"
            onSubmit={(event) => {
              event.preventDefault();
              list.handleFiltersChange({ search });
            }}
          >
            <CFormLabel htmlFor="role-title-search" className="mb-0">
              Title
            </CFormLabel>
            <CFormInput
              id="role-title-search"
              aria-label="Search by role title"
              className="flex-grow-1"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <CButton type="submit" color="primary">
              Search
            </CButton>
            <ClearFiltersButton
              fullWidth={false}
              className="text-nowrap"
              disabled={!search && !list.filters.search}
              onClick={() => {
                setSearch('');
                list.handleFiltersChange({ search: '' });
              }}
            />
          </CForm>
          <CTable responsive>
            <CTableHead>
              <CTableRow>
                <CTableHeaderCell>Title</CTableHeaderCell>
                <CTableHeaderCell>Level</CTableHeaderCell>
                <CTableHeaderCell>Assigned Users</CTableHeaderCell>
                {canEdit && <CTableHeaderCell>Action</CTableHeaderCell>}
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {list.items.map((role) => (
                <CTableRow key={role.id}>
                  <CTableDataCell>{role.name}</CTableDataCell>
                  <CTableDataCell>{role.level}</CTableDataCell>
                  <CTableDataCell>
                    <Link
                      to={`/users?${new URLSearchParams({ role: role.name }).toString()}`}
                      aria-label={`View users assigned to ${role.name}`}
                    >
                      {role.usersCount}
                    </Link>
                  </CTableDataCell>
                  {canEdit && (
                    <CTableDataCell>
                      <div className="d-flex gap-2">
                        {ownLevel !== null && role.level < ownLevel ? (
                          <Link className="btn btn-primary btn-sm" to={`/users/roles/${role.id}`}>
                            Edit
                          </Link>
                        ) : (
                          <CButton color="primary" size="sm" disabled>
                            Edit
                          </CButton>
                        )}
                        <CButton
                          color="danger"
                          size="sm"
                          disabled={
                            ownLevel === null || role.level >= ownLevel || role.usersCount > 0
                          }
                          onClick={() => list.handleDelete(role.id)}
                        >
                          Delete
                        </CButton>
                      </div>
                    </CTableDataCell>
                  )}
                </CTableRow>
              ))}
              {!list.loading && list.items.length === 0 && (
                <CTableRow>
                  <CTableDataCell colSpan={canEdit ? 4 : 3}>No roles found.</CTableDataCell>
                </CTableRow>
              )}
            </CTableBody>
          </CTable>
          <Pagination
            currentPage={list.currentPage}
            totalPages={list.lastPage}
            perPage={list.perPage}
            total={list.total}
            onPageChange={list.handlePageChange}
            onPerPageChange={list.handlePerPageChange}
          />
        </CCardBody>
      </CCard>
    </PageLayout>
  );
};
